import React from "react";
import { withI18n } from "react-i18next";
import styled from "styled-components";
import { connect } from "react-redux";

import { emailValidation, passwordValidation } from "../../helpers/validations";
import {
  Section,
  SectionTitle,
  StyledInput,
  StyledBlueBtn,
  SmallText,
  Spinner
} from "../Common/StyledComponents";
import PasswordInput from "../Common/PasswordInput";
import Typography from "../Common/Typography";
import { iRootState } from "../../store";
import {
  IActorCredentials,
  IActorError
} from "../../shared/api/dtos/IActorDto";
import TermUsePrivacy from "../Common/TermUsePrivacy";
import { errorPageRedirect, PasswordLength } from "../../helpers";

interface IProps extends StateProps, DispatchProps {
  t?: any;
  history?: any;
  location?: any;
  countryCode: string;
}

interface IState {
  fields: IActorCredentials;
  errors: IActorError;
  isFormSubmitClicked: boolean;
}

class TalentCredentialStep extends React.Component<IProps, IState> {
  public state: IState = {
    fields: {
      email: "",
      password: ""
    } as IActorCredentials,
    errors: this.props.errors || ({} as IActorError),
    isFormSubmitClicked: false
  };

  componentDidUpdate(prevProps: IProps) {
    if (this.props.errors !== prevProps.errors) {
      this.setState({ errors: this.props.errors });
    }
  }

  checkValidation() {
    const { t } = this.props;
    let fields = this.state.fields;
    let errors: IActorError = {} as IActorError;

    if (emailValidation(fields.email, t))
      errors.Email = emailValidation(fields.email, t);

    if (passwordValidation(fields.password, PasswordLength, t))
      errors.Password = passwordValidation(fields.password, PasswordLength, t);

    this.props.setErrors(errors);
    return Object.entries(errors).length === 0 && errors.constructor === Object;
  }

  handleChange = (e: any) => {
    let fields = this.state.fields;
    fields[e.target.name] = e.target.value;
    this.setState({ fields });
    this.state.isFormSubmitClicked && this.checkValidation();
  };

  createAccount = async (e: any) => {
    e.preventDefault();
    this.setState({ isFormSubmitClicked: true });
    let fields = this.state.fields;
    if (!this.checkValidation()) return;

    this.props.setActorCredentials(fields);
    await this.props.createActorAccount(fields);
    this.props.setEmailVerified(false);

    this.moveToNext();
  };

  moveToNext = () => {
    if (this.props.errors.errorMessage && this.props.errors.unknownError)
      errorPageRedirect();
    else if (
      !this.props.errors ||
      (Object.entries(this.props.errors).length === 0 &&
        this.props.errors.constructor === Object)
    ) {
      this.props.history.push({
        pathname: `/actor/1`,
        search: this.props.location.search
      });
    }
  };

  render() {
    const { t } = this.props;
    const errors = this.state.errors;
    const fields = this.state.fields;

    return (
      <Wrapper>
        <form
          name="registerForm"
          className="registerForm"
          onSubmit={this.createAccount}
          style={{ width: "100%" }}
        >
          <fieldset>
            <Section>
              <SectionTitle>{t("Email address")}</SectionTitle>
              <StyledInput
                name="email"
                type="text"
                autoComplete="off"
                className={errors.Email ? "invalid" : ""}
                onChange={this.handleChange}
                value={fields.email}
              />
              <SmallText className="error">
                <span>{errors.Email}</span>
              </SmallText>
            </Section>
            <Section>
              <SectionTitle>{t("Password")}</SectionTitle>
              <PasswordInput
                name="password"
                autoComplete="new-password"
                className={errors.Password ? "invalid" : ""}
                onChange={this.handleChange}
                value={fields.password}
              />
              {!errors.Password && (
                <SmallText>
                  <span>{t("Must contain at least 6 characters, 1 letter and 1 number")}</span>
                </SmallText>
              )}
              <SmallText className="error">
                <span>{errors.Password}</span>
              </SmallText>
            </Section>
            <Section>
              {errors.errorMessage ? (
                <SmallText className="error">
                  {errors.errorMessage}
                  <br />
                  <br />
                </SmallText>
              ) : (
                ""
              )}
              <StyledBlueBtn type="submit" id="submit" disabled={this.props.isLoading}>
                {t("Continue")}
                {this.props.isLoading && (
                  <Spinner
                    src="../images/spinner.svg"
                    className="spinner-width"
                  />
                )}
              </StyledBlueBtn>
            </Section>
            <Typography style={{ color: "#505050", fontWeight: 400, fontSize: "9px", lineHeight: "16px", letterSpacing: "0%" }}>
              {t("For new or lite members or those not on a PRO Account in the past six months.")}
            </Typography>
          </fieldset>
        </form>
        <TermUsePrivacy />
      </Wrapper>
    );
  }
}

interface StateProps {
  errors: IActorError;
  isLoading: boolean;
}

function mapStateToProps(state: iRootState): StateProps {
  return {
    errors: state.actorModel.errors,
    isLoading: state.actorModel.isLoading
  };
}

interface DispatchProps {
  createActorAccount: (fields: IActorCredentials) => void;
  setErrors: (errors: IActorError) => void;
  setActorCredentials: (actorCredentials: any) => void;
  setEmailVerified: (isEmailVerified: boolean) => void;
}

function mapDispatchToProps(dispatch: any): DispatchProps {
  return {
    createActorAccount: dispatch.actorModel.createActorAccount,
    setErrors: dispatch.actorModel.setErrors,
    setActorCredentials: dispatch.actorModel.setActorCredentials,
    setEmailVerified: dispatch.actorModel.setEmailVerified,
  };
}

export default withI18n()(
  connect(mapStateToProps, mapDispatchToProps)(TalentCredentialStep)
);

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: center;

  * {
    width: 100%;
  }

  & input {
    font-size: ${p => p.theme["s-font-size"]};
  }
`;
